import { useQuery } from "@apollo/client";
import { Box, LinearProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import React from "react";
import { REPORT_ENGAGEMENT } from "./dashboardReports.queries";
import { EVENT_LABELS, EventType } from "./eventTypes";
import { NewReturningChart } from "./NewReturningChart";
import { formatCount, formatDuration, formatPeriod } from "./reportFormat";
import { rangeVariables, ReportFilterState } from "./ReportFilters";
import { ResultView } from "./reportParams";
import { ReportError, Section, StatTile, TileRow } from "./reportParts";
import { EngagementPdfButton } from "./pdf/exportButtons";

/** The order the event tiles are shown in: what the card asked about first. */
const TILE_EVENTS: EventType[] = [
  "REGISTRATION",
  "REFERRAL",
  "RECOMMENDATION",
  "LEVEL_CHANGE",
  "TEAM_JOINED",
  "TEAM_MESSAGE",
  "INVITE_SHARED",
  "APP_OPEN",
];

export const EngagementTab: React.FC<{
  filters: ReportFilterState;
  view: ResultView;
  onOpenEvents: (type: EventType) => void;
}> = ({ filters, view, onOpenEvents }) => {
  const { data, loading, error } = useQuery(REPORT_ENGAGEMENT, {
    variables: { range: rangeVariables(filters) },
    fetchPolicy: "cache-and-network",
  });
  const report = data?.reportEngagement;
  const totals = report?.totals;
  const counts = new Map<string, number>((report?.events ?? []).map((event: any) => [event.type, event.count]));

  return (
    <Stack spacing={2}>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <EngagementPdfButton report={report && !loading ? report : null} filters={filters} />
      </Box>
      <ReportError error={error} />
      {loading && <LinearProgress />}
      {totals && (
        <>
          <Section
            title="Members"
            note="Active means the member opened the app or used it in the range. New members registered in it; returning members registered before it began."
          >
            <TileRow>
              <StatTile label="Active members" value={formatCount(totals.activeMembers)} />
              <StatTile
                label="New members"
                value={formatCount(totals.newMembers)}
                caption={totals.activeMembers ? `${Math.round((totals.newMembers / totals.activeMembers) * 100)}% of active` : undefined}
              />
              <StatTile
                label="Returning members"
                value={formatCount(totals.returningMembers)}
                caption={
                  totals.activeMembers ? `${Math.round((totals.returningMembers / totals.activeMembers) * 100)}% of active` : undefined
                }
              />
            </TileRow>
          </Section>

          <Section
            title="Time in the app"
            note="A session is a run of activity with no gap over 30 minutes. Time counts from the first to the last screen of each session, so it is a minimum: the app records when a screen opens, not when it closes."
          >
            <TileRow>
              <StatTile label="Sessions" value={formatCount(totals.sessions)} caption={`${totals.averageSessionsPerMember.toFixed(1)} per active member`} />
              <StatTile label="Average session" value={formatDuration(totals.averageSessionSeconds)} />
              <StatTile label="Time per active member" value={formatDuration(totals.averageEngagementSecondsPerMember)} />
              <StatTile label="Total time" value={formatDuration(totals.engagementSeconds)} />
              <StatTile label="Screen views" value={formatCount(totals.screenViews)} caption="Each screen counted once" />
            </TileRow>
          </Section>

          <Section title="What members did" note="Click a number to see who did it.">
            <TileRow>
              {TILE_EVENTS.map((type) => (
                <StatTile
                  key={type}
                  label={EVENT_LABELS[type].plural}
                  value={formatCount(counts.get(type))}
                  caption={type === "LEVEL_CHANGE" ? "Recorded from Oct 20, 2026" : undefined}
                  onClick={() => onOpenEvents(type)}
                />
              ))}
            </TileRow>
          </Section>

          <Section
            title="New and returning members"
            note={view === "chart" ? "Hover a bar for its numbers. The table view has the same figures." : "Newest first."}
          >
            {view === "chart" ? (
              <NewReturningChart points={report.periods} granularity={filters.granularity} />
            ) : (
              <Box sx={{ overflow: "auto", maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Period</TableCell>
                      <TableCell align="right">Active</TableCell>
                      <TableCell align="right">New</TableCell>
                      <TableCell align="right">Returning</TableCell>
                      <TableCell align="right">Sessions</TableCell>
                      <TableCell align="right">Time in app</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...report.periods].reverse().map((period: any) => (
                      <TableRow key={period.period}>
                        <TableCell>{formatPeriod(period.period, filters.granularity)}</TableCell>
                        <TableCell align="right">{formatCount(period.activeMembers)}</TableCell>
                        <TableCell align="right">{formatCount(period.newMembers)}</TableCell>
                        <TableCell align="right">{formatCount(period.returningMembers)}</TableCell>
                        <TableCell align="right">{formatCount(period.sessions)}</TableCell>
                        <TableCell align="right">{formatDuration(period.engagementSeconds)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Section>
        </>
      )}
    </Stack>
  );
};
